/** Beat Shop admin: review queue · catalog · orders · finance · config · audit. */
import React, { useCallback, useEffect, useState } from 'react';
import * as api from '../api';
import { fmtCents, fmtPoints } from '../types';
import { usePreviewPlayer, PreviewPlayerProvider } from '../PreviewPlayer';
import { InfoTip, PageShell, PrimaryBtn, GhostBtn, cardPanel, fileDropProps, inputStyle, labelStyle, s, segOff, segOn } from '../ui';
import Modal from '../../../components/UI/Modal';

type Tab = 'queue' | 'catalog' | 'orders' | 'finance' | 'config' | 'audit';

const AdminPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('queue');
  return (
    <PreviewPlayerProvider>
      <PageShell>
        <div style={s('font-size:11.5px;font-weight:600;letter-spacing:0.15em;color:var(--bs-text-label);margin-bottom:11px')}>ADMIN</div>
        <h1 style={s('margin:0 0 24px;font-weight:700;font-size:36px;letter-spacing:-0.025em;color:var(--bs-text)')}>Beat Shop Console</h1>
        <div style={s('display:flex;background:var(--bs-surface);border:1px solid var(--bs-border);border-radius:9px;padding:3px;gap:2px;width:fit-content;margin-bottom:26px;flex-wrap:wrap')}>
          {(['queue', 'catalog', 'orders', 'finance', 'config', 'audit'] as Tab[]).map((tb) => (
            <button key={tb} onClick={() => setTab(tb)} style={s(tab === tb ? segOn : segOff)}>
              {tb === 'queue' ? 'Review Queue' : tb[0].toUpperCase() + tb.slice(1)}
            </button>
          ))}
        </div>
        {tab === 'queue' && <QueueTab />}
        {tab === 'catalog' && <CatalogTab />}
        {tab === 'orders' && <OrdersTab />}
        {tab === 'finance' && <FinanceTab />}
        {tab === 'config' && <ConfigTab />}
        {tab === 'audit' && <AuditTab />}
      </PageShell>
    </PreviewPlayerProvider>
  );
};

const rowGrid = (cols: string) => s(`display:grid;grid-template-columns:${cols};gap:14px;align-items:center;padding:13px 10px;border-bottom:1px solid var(--bs-line)`);
const headGrid = (cols: string) => s(`display:grid;grid-template-columns:${cols};gap:14px;padding:0 10px 12px;border-bottom:1px solid var(--bs-border)`);
const th = s('font-size:11px;font-weight:600;letter-spacing:0.08em;color:var(--bs-text-label)');

const PAGE_SIZE = 25;

function pagesWindow(current: number, totalPages: number): (number | '…')[] {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
  const out: (number | '…')[] = [];
  const push = (v: number | '…') => { if (out.length === 0 || out[out.length - 1] !== v) out.push(v); };
  push(0);
  const start = Math.max(1, current - 1);
  const end = Math.min(totalPages - 2, current + 1);
  if (start > 1) push('…');
  for (let i = start; i <= end; i++) push(i);
  if (end < totalPages - 2) push('…');
  push(totalPages - 1);
  return out;
}

/** Same control as Beat Shop browse: range + Prev + numbered pages + Next. */
const ListPagination: React.FC<{
  page: number;
  total: number;
  pageSize?: number;
  onPage: (page: number) => void;
}> = ({ page, total, pageSize = PAGE_SIZE, onPage }) => {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const lastPage = pageCount - 1;
  return (
    <div style={s('display:flex;align-items:center;justify-content:space-between;gap:14px;padding:18px 10px 6px;flex-wrap:wrap')}>
      <span style={s('font-size:12.5px;color:var(--bs-text-muted);font-variant-numeric:tabular-nums')}>
        {total === 0
          ? '0 of 0'
          : `${page * pageSize + 1}–${Math.min((page + 1) * pageSize, total)} of ${total}`}
      </span>
      <div style={s('display:flex;align-items:center;gap:9px')}>
        <GhostBtn onClick={() => onPage(Math.max(0, page - 1))} disabled={page === 0} style="padding:6px 12px;font-size:12px">
          ← Prev
        </GhostBtn>
        <div style={s('display:flex;align-items:center;gap:6px;flex-wrap:wrap')}>
          {pagesWindow(page, pageCount).map((v, i) =>
            v === '…' ? (
              <span key={`e-${i}`} style={s('font-size:12.5px;color:var(--bs-text-label);padding:0 4px')}>…</span>
            ) : (
              <GhostBtn
                key={v}
                onClick={() => onPage(v)}
                disabled={v === page}
                style={`padding:6px 10px;font-size:12px;${v === page ? 'opacity:0.7' : ''}`}
              >
                {v + 1}
              </GhostBtn>
            )
          )}
        </div>
        <GhostBtn onClick={() => onPage(Math.min(lastPage, page + 1))} disabled={page >= lastPage} style="padding:6px 12px;font-size:12px">
          Next →
        </GhostBtn>
      </div>
    </div>
  );
};

/** Only one <audio> in the review queue plays at a time. */
const pauseOtherQueueAudio = (e: React.SyntheticEvent<HTMLAudioElement>) => {
  const root = e.currentTarget.closest('[data-bs-queue-audio]');
  if (!root) return;
  root.querySelectorAll('audio').forEach((el) => {
    if (el !== e.currentTarget) (el as HTMLAudioElement).pause();
  });
};

const QueueTab: React.FC = () => {
  const [queue, setQueue] = useState<any[] | null>(null);
  const [listedEdits, setListedEdits] = useState<any[]>([]);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<{ id: string; decision: 'approve' | 'reject'; kind: 'new' | 'listed_edit' } | null>(null);
  const load = useCallback(() => api.adminGetReviewQueue().then((r) => {
    setQueue(r.queue || []);
    setListedEdits(r.listed_edits || []);
  }).catch(() => { setQueue([]); setListedEdits([]); }), []);
  useEffect(() => { load(); }, [load]);

  const decide = async (beatId: string, decision: 'approve' | 'reject') => {
    if (busy) return;
    setBusy({ id: beatId, decision, kind: 'new' });
    try {
      await api.adminDecide(beatId, decision, decision === 'reject' ? reason : undefined);
      setRejecting(null); setReason('');
      await load();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setBusy(null);
    }
  };

  const decideListedEdit = async (editId: string, decision: 'approve' | 'reject') => {
    if (busy) return;
    setBusy({ id: editId, decision, kind: 'listed_edit' });
    try {
      await api.adminDecideListedEdit(editId, decision, decision === 'reject' ? reason : undefined);
      setRejecting(null); setReason('');
      await load();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setBusy(null);
    }
  };

  if (queue === null) return <div style={s('color:var(--bs-text-muted)')}>Loading…</div>;
  if (queue.length === 0 && listedEdits.length === 0) {
    return <div style={s('color:var(--bs-text-muted);padding:30px 0')}>Review queue is empty.</div>;
  }

  const metaRow = (label: string, liveVal: React.ReactNode, newVal: React.ReactNode, changed: boolean) => (
    <div style={s(`display:grid;grid-template-columns:110px 1fr 1fr;gap:10px;padding:8px 0;border-bottom:1px solid var(--bs-line);font-size:13px;${changed ? 'background:rgba(47,107,255,0.06)' : ''}`)}>
      <div style={s('color:var(--bs-text-label);font-weight:600')}>{label}</div>
      <div style={s('color:var(--bs-text-body)')}>{liveVal}</div>
      <div style={s('color:var(--bs-text);font-weight:600')}>{newVal}</div>
    </div>
  );

  return (
    <div data-bs-queue-audio style={s('display:flex;flex-direction:column;gap:16px')}>
      {listedEdits.map((e) => {
        const rowBusy = busy?.id === e.id && busy?.kind === 'listed_edit';
        const approving = rowBusy && busy?.decision === 'approve';
        const rejectingBusy = rowBusy && busy?.decision === 'reject';
        const live = e.live || {};
        const pending = e.pending || {};
        const genresLive = (live.genres || []).join(', ');
        const genresNew = (pending.genres || []).join(', ');
        return (
          <div key={e.id} style={s(cardPanel + 'padding:20px')}>
            <div style={s('font-size:11px;font-weight:700;letter-spacing:0.1em;color:var(--bs-blue-soft);margin-bottom:10px')}>
              LISTED BEAT EDIT{e.replace_audio ? ' · AUDIO REPLACE' : ' · METADATA ONLY'}
            </div>
            <div style={s('font-weight:700;font-size:18px;margin-bottom:4px')}>{pending.name || live.name}</div>
            <div style={s('font-size:12.5px;color:var(--bs-text-muted);margin-bottom:14px')}>
              by {e.seller_name || `artist #${e.seller_artist_id}`} · live listing stays published until you decide
            </div>
            <div style={s('display:grid;grid-template-columns:110px 1fr 1fr;gap:10px;padding:8px 0;border-bottom:1px solid var(--bs-border);font-size:11px;font-weight:700;letter-spacing:0.06em;color:var(--bs-text-label)')}>
              <div>FIELD</div>
              <div>CURRENT (LIVE)</div>
              <div>PROPOSED</div>
            </div>
            {metaRow('Name', live.name, pending.name, live.name !== pending.name)}
            {metaRow('BPM', live.bpm, pending.bpm, Number(live.bpm) !== Number(pending.bpm))}
            {metaRow('Key', live.musical_key, pending.musical_key, live.musical_key !== pending.musical_key)}
            {metaRow('Genres', genresLive, genresNew, genresLive !== genresNew)}
            {metaRow('Points', fmtPoints(Number(live.price_points)), fmtPoints(Number(pending.price_points)), Number(live.price_points) !== Number(pending.price_points))}
            {metaRow('Cash', fmtCents(Number(live.price_cents)), fmtCents(Number(pending.price_cents)), Number(live.price_cents) !== Number(pending.price_cents))}
            {e.replace_audio ? (
              <div style={s('display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:16px')}>
                <div>
                  <div style={s('font-size:11px;font-weight:700;letter-spacing:0.08em;color:var(--bs-text-label);margin-bottom:8px')}>CURRENT AUDIO</div>
                  {live.preview_url && (
                    <div style={s('margin-bottom:10px')}>
                      <div style={s('font-size:11px;color:var(--bs-text-muted);margin-bottom:4px')}>Preview</div>
                      <audio controls src={live.preview_url} onPlay={pauseOtherQueueAudio} style={{ width: '100%', height: 36 }} />
                    </div>
                  )}
                  {live.full_audio_url && (
                    <div>
                      <div style={s('font-size:11px;color:var(--bs-text-muted);margin-bottom:4px')}>Full audio</div>
                      <audio controls src={live.full_audio_url} onPlay={pauseOtherQueueAudio} style={{ width: '100%', height: 36 }} />
                    </div>
                  )}
                  {!live.preview_url && !live.full_audio_url && <div style={s('font-size:12px;color:var(--bs-text-muted)')}>No audio</div>}
                </div>
                <div>
                  <div style={s('font-size:11px;font-weight:700;letter-spacing:0.08em;color:var(--bs-text-label);margin-bottom:8px')}>PROPOSED AUDIO</div>
                  {pending.preview_url && (
                    <div style={s('margin-bottom:10px')}>
                      <div style={s('font-size:11px;color:var(--bs-text-muted);margin-bottom:4px')}>Preview</div>
                      <audio controls src={pending.preview_url} onPlay={pauseOtherQueueAudio} style={{ width: '100%', height: 36 }} />
                    </div>
                  )}
                  {pending.full_audio_url && (
                    <div>
                      <div style={s('font-size:11px;color:var(--bs-text-muted);margin-bottom:4px')}>Full audio</div>
                      <audio controls src={pending.full_audio_url} onPlay={pauseOtherQueueAudio} style={{ width: '100%', height: 36 }} />
                    </div>
                  )}
                  {!pending.preview_url && !pending.full_audio_url && <div style={s('font-size:12px;color:var(--bs-text-muted)')}>No audio</div>}
                </div>
              </div>
            ) : (
              <div style={s('margin-top:14px;font-size:12.5px;color:var(--bs-text-muted);line-height:1.45')}>
                Metadata only — live audio is unchanged.
              </div>
            )}
            <div style={s('display:flex;flex-direction:column;gap:9px;margin-top:16px;max-width:280px')}>
              <PrimaryBtn onClick={() => decideListedEdit(e.id, 'approve')} disabled={!!rowBusy} style="padding:10px 16px;font-size:13px">
                {approving ? 'Approving…' : 'Approve → apply edit'}
              </PrimaryBtn>
              {rejecting === e.id ? (
                <div style={s('display:flex;flex-direction:column;gap:8px')}>
                  <input value={reason} onChange={(ev) => setReason(ev.target.value)} placeholder="Rejection reason (required)" disabled={!!rowBusy} style={s(inputStyle)} />
                  <div style={s('display:flex;gap:8px')}>
                    <GhostBtn onClick={() => decideListedEdit(e.id, 'reject')} disabled={!!rowBusy || !reason.trim()} style="padding:9px 14px;font-size:12.5px;flex:1">
                      {rejectingBusy ? 'Rejecting…' : 'Confirm reject'}
                    </GhostBtn>
                    <GhostBtn onClick={() => { setRejecting(null); setReason(''); }} disabled={!!rowBusy} style="padding:9px 14px;font-size:12.5px">✕</GhostBtn>
                  </div>
                </div>
              ) : (
                <GhostBtn onClick={() => setRejecting(e.id)} disabled={!!rowBusy} style="padding:9px 14px;font-size:12.5px">Reject (keep live)</GhostBtn>
              )}
            </div>
          </div>
        );
      })}

      {queue.map((b) => {
        const rowBusy = busy?.id === b.id && busy?.kind === 'new';
        const approving = rowBusy && busy?.decision === 'approve';
        const rejectingBusy = rowBusy && busy?.decision === 'reject';
        return (
          <div key={b.id} style={s(cardPanel + 'padding:20px')}>
            <div style={s('font-size:11px;font-weight:700;letter-spacing:0.1em;color:var(--bs-text-label);margin-bottom:10px')}>NEW SUBMISSION</div>
            <div style={s('display:flex;align-items:flex-start;gap:16px;flex-wrap:wrap')}>
              <div style={s('flex:1;min-width:260px')}>
                <div style={s('font-weight:700;font-size:19px')}>{b.name}</div>
                <div style={s('font-size:12.5px;color:var(--bs-text-muted);margin-top:4px')}>
                  by {b.seller_name || `artist #${b.seller_artist_id}`} · {b.bpm} BPM · {b.musical_key} · {(b.genres || []).join(', ')}
                </div>
                <div style={s('font-size:12.5px;color:var(--bs-text-muted);margin-top:2px')}>{fmtPoints(Number(b.price_points))} / {fmtCents(Number(b.price_cents))}</div>
                {b.full_audio_url && (
                  <audio controls src={b.full_audio_url} onPlay={pauseOtherQueueAudio} style={{ ...s('margin-top:14px;width:100%;max-width:420px'), height: 36 }} />
                )}
              </div>
              <div style={s('display:flex;flex-direction:column;gap:9px;min-width:180px')}>
                <PrimaryBtn
                  onClick={() => decide(b.id, 'approve')}
                  disabled={!!rowBusy}
                  style="padding:10px 16px;font-size:13px"
                >
                  {approving ? 'Approving…' : 'Approve → list'}
                </PrimaryBtn>
                {rejecting === b.id ? (
                  <div style={s('display:flex;flex-direction:column;gap:8px')}>
                    <input
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Rejection reason (required)"
                      disabled={!!rowBusy}
                      style={s(inputStyle + (rowBusy ? 'opacity:0.6;cursor:not-allowed;' : ''))}
                    />
                    <div style={s('display:flex;gap:8px')}>
                      <GhostBtn
                        onClick={() => decide(b.id, 'reject')}
                        disabled={!!rowBusy || !reason.trim()}
                        style="padding:9px 14px;font-size:12.5px;flex:1"
                      >
                        {rejectingBusy ? 'Rejecting…' : 'Confirm reject'}
                      </GhostBtn>
                      <GhostBtn
                        onClick={() => { setRejecting(null); setReason(''); }}
                        disabled={!!rowBusy}
                        style="padding:9px 14px;font-size:12.5px"
                      >
                        ✕
                      </GhostBtn>
                    </div>
                  </div>
                ) : (
                  <GhostBtn
                    onClick={() => setRejecting(b.id)}
                    disabled={!!rowBusy}
                    style="padding:10px 16px;font-size:13px"
                  >
                    Reject…
                  </GhostBtn>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const MAX_PREVIEW_MB = 10;

/** Per-row preview upload state. Rows upload independently and concurrently. */
type PreviewState = { busy?: boolean; error?: string; ok?: string };

const CatalogTab: React.FC = () => {
  const player = usePreviewPlayer();
  const [beats, setBeats] = useState<any[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState('');
  const [preview, setPreview] = useState<Record<string, PreviewState>>({});

  const FAVORITES_PAGE_SIZE = 25;
  const [favoritesModal, setFavoritesModal] = useState<null | { beatId: string; beatName: string }>(null);
  const [favoritesPage, setFavoritesPage] = useState(0);
  const [favoritesTotal, setFavoritesTotal] = useState(0);
  const [favoritesUsers, setFavoritesUsers] = useState<{ userId: number; name: string }[]>([]);
  const [favoritesBusy, setFavoritesBusy] = useState(false);

  useEffect(() => { setPage(0); }, [status]);

  const load = useCallback(() => api.adminGetBeats({
    status: status || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE,
  }).then((r) => { setBeats(r.beats); setTotal(r.total || 0); })
    .catch(() => { setBeats([]); setTotal(0); }), [status, page]);
  useEffect(() => { load(); }, [load]);

  // Queue mini-player with preview URLs only (never master / full audio).
  useEffect(() => {
    if (!beats) {
      player.setQueue([]);
      return;
    }
    player.setQueue(beats
      .filter((b) => !!b.preview_url)
      .map((b) => ({
        id: b.id,
        name: b.name,
        preview_url: b.preview_url,
        bpm: b.bpm,
        musical_key: b.musical_key,
        seller_name: b.seller_name,
        is_favorited: false,
      })));
  }, [beats, player.setQueue]);

  useEffect(() => () => {
    player.setQueue([]);
    player.stop();
  }, [player.setQueue, player.stop]);

  const togglePlay = (beat: any) => {
    if (!beat.preview_url) return;
    player.toggle({
      id: beat.id,
      name: beat.name,
      preview_url: beat.preview_url,
      bpm: beat.bpm,
      musical_key: beat.musical_key,
      seller_name: beat.seller_name,
      is_favorited: false,
    });
  };

  const act = async (beatId: string, action: 'delist' | 'relist' | 'takedown') => {
    const reason = action === 'takedown' ? window.prompt('Takedown reason (required):') || '' : '';
    if (action === 'takedown' && !reason) return;
    try { await api.adminBeatAction(beatId, action, reason); load(); } catch (e: any) { alert(e.message); }
  };

  const loadFavorites = useCallback(() => {
    if (!favoritesModal) return;
    setFavoritesBusy(true);
    api.adminGetBeatFavoriters(favoritesModal.beatId, {
      limit: FAVORITES_PAGE_SIZE,
      offset: favoritesPage * FAVORITES_PAGE_SIZE,
    })
      .then((r) => {
        setFavoritesUsers(r.users);
        setFavoritesTotal(r.total);
      })
      .catch(() => {
        setFavoritesUsers([]);
        setFavoritesTotal(0);
      })
      .finally(() => setFavoritesBusy(false));
  }, [favoritesModal, favoritesPage, FAVORITES_PAGE_SIZE]);

  useEffect(() => {
    if (!favoritesModal) return;
    setFavoritesPage(0);
  }, [favoritesModal]);

  useEffect(() => {
    if (!favoritesModal) return;
    loadFavorites();
  }, [favoritesModal, favoritesPage, loadFavorites]);

  /** Upload one row's preview. Never reloads the page or the list — only this row. */
  const uploadPreview = async (beatId: string, file: File) => {
    if (file.size > MAX_PREVIEW_MB * 1024 * 1024) {
      setPreview((p) => ({
        ...p,
        [beatId]: { error: `That file is ${(file.size / 1048576).toFixed(1)}MB — the limit is ${MAX_PREVIEW_MB}MB` },
      }));
      return;
    }
    setPreview((p) => ({ ...p, [beatId]: { busy: true } }));
    try {
      const r = await api.adminReplacePreview(beatId, file);
      // swap in the freshly signed URL so it is immediately audible in place
      setBeats((prev) => prev
        ? prev.map((b) => (b.id === beatId ? { ...b, preview_url: r.preview_url } : b))
        : prev);
      if (player.isCurrent(beatId)) player.stop();
      setPreview((p) => ({ ...p, [beatId]: { ok: `Replaced · ${r.durationSeconds}s` } }));
    } catch (e: any) {
      setPreview((p) => ({ ...p, [beatId]: { error: e.message || 'Upload failed' } }));
    }
  };

  const cols = '1fr 100px 110px 130px 140px 210px 200px';
  const iconBtn = 'display:inline-flex;align-items:center;justify-content:center;gap:6px;padding:6px 11px;font-size:12px;border-radius:8px;border:1px solid var(--bs-border-2);background:transparent;cursor:pointer;';

  return (
    <div>
      <select
        className="bs-admin-catalog-filter"
        value={status}
        onChange={(e) => { setStatus(e.target.value); player.stop(); }}
        style={{ ...s(inputStyle + ';appearance:none;margin-bottom:16px'), width: 220 }}
      >
        <option value="">All statuses</option>
        {['pending_review', 'listed', 'reserved', 'sold', 'delisted', 'rejected', 'takedown', 'draft', 'processing'].map((st) => <option key={st} value={st}>{st}</option>)}
      </select>

      <div className="bs-admin-catalog-head" style={headGrid(cols)}>
        <span style={th}>BEAT</span>
        <span style={th}>STATUS</span>
        <span className="bs-admin-cat-desktop" style={th}>PRICE ✦</span>
        <span className="bs-admin-cat-desktop" style={th}>SELLER</span>
        <span className="bs-admin-cat-favorites" style={th}>FAVORITED BY</span>
        <span style={th}>PREVIEW</span>
        <span style={th}>ACTIONS</span>
      </div>

      {beats === null && <div style={s('color:var(--bs-text-muted);padding:24px 10px')}>Loading…</div>}
      {beats !== null && beats.length === 0 && (
        <div style={s('color:var(--bs-text-muted);padding:24px 10px')}>No beats match this filter.</div>
      )}

      {(beats || []).map((b) => {
        const ps = preview[b.id] || {};
        const isPlaying = player.isPlayingId(b.id);
        return (
          <div key={b.id} className="bs-admin-catalog-row" style={rowGrid(cols)}>
            <div>
              <div style={s('font-weight:600;font-size:14px;color:var(--bs-text)')}>{b.name}</div>
              <div style={s('font-size:11.5px;color:var(--bs-text-label)')}>{b.bpm} BPM · {b.musical_key}</div>
              <div className="bs-admin-cat-mobile-meta" style={s('font-size:11px;color:var(--bs-text-label);margin-top:2px')}>
                {Number(b.price_points).toLocaleString()} ✦ · {b.seller_name || `#${b.seller_artist_id}`}
              </div>
            </div>
            <span className="bs-admin-cat-status" style={s('font-size:12px;color:var(--bs-text-body)')}>{b.status}</span>
            <span className="bs-admin-cat-desktop" style={s('font-size:12.5px;color:var(--bs-text-body)')}>{Number(b.price_points).toLocaleString()}</span>
            <span className="bs-admin-cat-desktop" style={s('font-size:12.5px;color:var(--bs-text-body)')}>{b.seller_name || `#${b.seller_artist_id}`}</span>

            <div className="bs-admin-cat-favorites">
              <GhostBtn
                onClick={() => setFavoritesModal({ beatId: b.id, beatName: b.name })}
                style="padding:6px 11px;font-size:12px"
              >
                {typeof b.favorite_count === 'number' ? b.favorite_count : 'View'}
              </GhostBtn>
            </div>

            <div className="bs-admin-cat-preview">
              <div style={s('display:flex;gap:7px;align-items:center')}>
                <button
                  type="button"
                  onClick={() => togglePlay(b)}
                  disabled={!b.preview_url}
                  title={b.preview_url ? 'Play current preview' : 'No preview on this beat'}
                  style={s(iconBtn + (b.preview_url
                    ? 'color:var(--bs-text-hi);'
                    : 'opacity:0.4;cursor:not-allowed;color:var(--bs-text-faint);'))}
                >
                  {isPlaying
                    ? <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor"><rect x="1.5" y="1" width="3" height="10" rx="1" /><rect x="7.5" y="1" width="3" height="10" rx="1" /></svg>
                    : <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor"><path d="M2.5 1v10l8-5z" /></svg>}
                  {isPlaying ? 'Pause' : 'Play'}
                </button>
                <label
                  {...fileDropProps((files) => {
                    const f = files[0];
                    if (!f || ps.busy) return;
                    const isMp3 = f.type === 'audio/mpeg' || f.name.toLowerCase().endsWith('.mp3');
                    if (!isMp3) return;
                    uploadPreview(b.id, f);
                  })}
                  style={s(iconBtn + (ps.busy
                    ? 'opacity:0.6;cursor:wait;color:var(--bs-text-faint);'
                    : 'color:var(--bs-blue-soft);'))}
                >
                  {ps.busy ? 'Uploading…' : 'Upload'}
                  <input
                    type="file"
                    accept=".mp3,audio/mpeg"
                    disabled={ps.busy}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = ''; // allow re-picking the same file after a failure
                      if (f) uploadPreview(b.id, f);
                    }}
                  />
                </label>
              </div>
              {ps.error && <div style={s('font-size:11px;color:var(--bs-red);margin-top:4px;line-height:1.35')}>{ps.error}</div>}
              {ps.ok && <div style={s('font-size:11px;color:var(--bs-green);margin-top:4px')}>{ps.ok}</div>}
            </div>

            <div className="bs-admin-cat-actions" style={s('display:flex;gap:7px')}>
              {b.status === 'listed' && <GhostBtn onClick={() => act(b.id, 'delist')} style="padding:6px 11px;font-size:12px">Delist</GhostBtn>}
              {(b.status === 'delisted' || b.status === 'takedown') && <GhostBtn onClick={() => act(b.id, 'relist')} style="padding:6px 11px;font-size:12px">Relist</GhostBtn>}
              {b.status !== 'takedown' && b.status !== 'sold' && <GhostBtn onClick={() => act(b.id, 'takedown')} style="padding:6px 11px;font-size:12px;color:var(--bs-red)">Takedown</GhostBtn>}
            </div>
          </div>
        );
      })}

      {beats !== null && (
        <ListPagination
          page={page}
          total={total}
          onPage={(p) => { player.stop(); setPage(p); }}
        />
      )}

      <Modal
        isOpen={!!favoritesModal}
        onClose={() => setFavoritesModal(null)}
        title={favoritesModal ? favoritesModal.beatName : undefined}
        size="md"
      >
        {favoritesBusy && (
          <div style={s('font-size:13px;color:var(--bs-text-muted)')}>Loading…</div>
        )}
        {!favoritesBusy && (
          <>
            <div style={s('font-size:13px;color:var(--bs-text-label);margin-bottom:10px')}>
              {favoritesTotal ? `${favoritesTotal} favoriters` : 'No favoriters'}
            </div>
            <div style={s('display:grid;grid-template-columns:1fr 1fr;gap:8px;max-height:320px;overflow:auto;padding-right:6px')}>
              {favoritesUsers.length === 0 ? (
                <div style={s('font-size:13px;color:var(--bs-text-muted);grid-column:1 / -1')}>—</div>
              ) : (
                favoritesUsers.map((u) => (
                  <div
                    key={u.userId}
                    style={s('font-size:13.5px;color:var(--bs-text-body);padding:10px 12px;border-radius:10px;border:1px solid #d1d5db;background:var(--bs-surface)')}
                  >
                    {u.name || `User #${u.userId}`}
                  </div>
                ))
              )}
            </div>

            <div style={s('display:flex;align-items:center;justify-content:space-between;gap:14px;margin-top:14px')}>
              <GhostBtn
                onClick={() => setFavoritesPage((p) => Math.max(0, p - 1))}
                disabled={favoritesPage === 0}
                style="padding:6px 12px;font-size:12px"
              >
                ← Prev
              </GhostBtn>

              <div style={s('display:flex;align-items:center;gap:6px;flex-wrap:wrap;justify-content:center')}>
                {(() => {
                  const pageCount = Math.max(1, Math.ceil(favoritesTotal / FAVORITES_PAGE_SIZE));
                  const last = pageCount - 1;
                  if (pageCount <= 7) {
                    return Array.from({ length: pageCount }, (_, i) => i).map((pIdx) => (
                      <GhostBtn
                        key={pIdx}
                        onClick={() => setFavoritesPage(pIdx)}
                        disabled={pIdx === favoritesPage}
                        style={`padding:6px 10px;font-size:12px;${pIdx === favoritesPage ? 'opacity:0.7' : ''}`}
                      >
                        {pIdx + 1}
                      </GhostBtn>
                    ));
                  }
                  const out: (number | '…')[] = [];
                  const push = (v: number | '…') => { if (out.length === 0 || out[out.length - 1] !== v) out.push(v); };
                  push(0);
                  const start = Math.max(1, favoritesPage - 1);
                  const end = Math.min(last - 1, favoritesPage + 1);
                  if (start > 1) push('…');
                  for (let i = start; i <= end; i++) push(i);
                  if (end < last - 1) push('…');
                  push(last);
                  return out.map((v, idx) =>
                    v === '…' ? (
                      <span key={`e-${idx}`} style={s('font-size:12.5px;color:var(--bs-text-label);padding:0 4px')}>…</span>
                    ) : (
                      <GhostBtn
                        key={v}
                        onClick={() => setFavoritesPage(v)}
                        disabled={v === favoritesPage}
                        style={`padding:6px 10px;font-size:12px;${v === favoritesPage ? 'opacity:0.7' : ''}`}
                      >
                        {v + 1}
                      </GhostBtn>
                    )
                  );
                })()}
              </div>

              <GhostBtn
                onClick={() => setFavoritesPage((p) => Math.min(Math.max(0, Math.ceil(favoritesTotal / FAVORITES_PAGE_SIZE) - 1), p + 1))}
                disabled={favoritesPage >= Math.max(0, Math.ceil(favoritesTotal / FAVORITES_PAGE_SIZE) - 1)}
                style="padding:6px 12px;font-size:12px"
              >
                Next →
              </GhostBtn>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

const OrdersTab: React.FC = () => {
  const [orders, setOrders] = useState<any[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const load = useCallback(() => api.adminGetOrders({
    limit: PAGE_SIZE, offset: page * PAGE_SIZE,
  }).then((r) => {
    setOrders(r.orders);
    setTotal(r.total || 0);
  }).catch(() => { setOrders([]); setTotal(0); }), [page]);
  useEffect(() => { load(); }, [load]);

  const reverse = async (orderId: string) => {
    const reason = window.prompt('Reversal reason (required):');
    if (!reason) return;
    const relist = window.confirm('Relist the beat after reversal? (OK = yes)');
    try { await api.adminReverseOrder(orderId, reason, relist); load(); } catch (e: any) { alert(e.message); }
  };

  const cols = '1fr 130px 110px 130px 130px 110px';
  return (
    <div>
      <div className="bs-admin-orders-head" style={headGrid(cols)}>
        <span style={th}>BEAT / PARTIES</span>
        <span className="bs-admin-orders-desktop" style={th}>TENDER</span>
        <span style={th}>STATUS</span>
        <span style={th}>GROSS</span>
        <span className="bs-admin-orders-desktop" style={th}>PLATFORM CUT</span>
        <span style={th}>ACTION</span>
      </div>
      {orders === null && <div style={s('color:var(--bs-text-muted);padding:24px 10px')}>Loading…</div>}
      {orders !== null && orders.length === 0 && (
        <div style={s('color:var(--bs-text-muted);padding:24px 10px')}>No orders.</div>
      )}
      {(orders || []).map((o) => (
        <div key={o.id} className="bs-admin-orders-row" style={rowGrid(cols)}>
          <div>
            <div style={s('font-weight:600;font-size:14px;color:var(--bs-text)')}>{o.beat_name}</div>
            <div style={s('font-size:11.5px;color:var(--bs-text-label)')}>{o.buyer_artist_name || `#${o.buyer_artist_id}`} ← {o.seller_artist_name || `#${o.seller_artist_id}`} · {new Date(o.created_at).toLocaleString()}</div>
            <div className="bs-admin-orders-mobile-meta" style={s('font-size:11px;color:var(--bs-text-label);margin-top:2px')}>
              {o.tender.replace('_', ' ')} · {o.commission_points != null ? fmtPoints(Number(o.commission_points)) : o.commission_cents != null ? fmtCents(Number(o.commission_cents)) : '—'} fee
            </div>
          </div>
          <span className="bs-admin-orders-desktop" style={s('font-size:12.5px;color:var(--bs-text-body)')}>{o.tender.replace('_', ' ')}</span>
          <span className="bs-admin-orders-status" style={{ ...s('font-size:12px;font-weight:600'), color: o.status === 'paid' ? 'var(--bs-green)' : o.status === 'reversed' ? 'var(--bs-red)' : 'var(--bs-text-body)' }}>{o.status}</span>
          <span className="bs-admin-orders-gross" style={s('font-size:12.5px;color:var(--bs-text-body);font-variant-numeric:tabular-nums')}>{o.amount_points != null ? fmtPoints(Number(o.amount_points)) : fmtCents(Number(o.amount_cents))}</span>
          <span className="bs-admin-orders-desktop" style={s('font-size:12.5px;color:var(--bs-text-body);font-variant-numeric:tabular-nums')}>{o.commission_points != null ? fmtPoints(Number(o.commission_points)) : o.commission_cents != null ? fmtCents(Number(o.commission_cents)) : '—'}</span>
          <div className="bs-admin-orders-action">{o.status === 'paid' && <GhostBtn onClick={() => reverse(o.id)} style="padding:6px 11px;font-size:12px;color:var(--bs-red)">Reverse</GhostBtn>}</div>
        </div>
      ))}
      {orders !== null && (
        <ListPagination page={page} total={total} onPage={setPage} />
      )}
    </div>
  );
};

const FinanceTab: React.FC = () => {
  const [fin, setFin] = useState<any | null>(null);
  const [recon, setRecon] = useState<any | null>(null);
  useEffect(() => {
    api.adminGetFinance().then(setFin).catch(() => {});
    api.adminGetReconciliation().then(setRecon).catch(() => {});
  }, []);
  return (
    <div>
      <div className="bs-admin-fin-kpis" style={s('display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:24px')}>
        {[
          { label: 'PAID ORDERS', value: fin?.summary?.paid_orders ?? '—' },
          { label: 'GROSS — POINTS', value: fin ? fmtPoints(Number(fin.summary.gross_points)) : '—' },
          { label: 'GROSS — CASH', value: fin ? fmtCents(Number(fin.summary.gross_cents)) : '—' },
          { label: 'COMMISSION (15%)', value: fin ? `${fmtPoints(Number(fin.summary.commission_points))} · ${fmtCents(Number(fin.summary.commission_cents))}` : '—' },
        ].map((c) => (
          <div key={c.label} style={s(cardPanel + 'padding:18px 19px;border-radius:16px')}>
            <div style={s('font-size:10.5px;font-weight:600;letter-spacing:0.13em;color:var(--bs-text-label);margin-bottom:14px')}>{c.label}</div>
            <div style={s('font-weight:700;font-size:20px')}>{c.value}</div>
          </div>
        ))}
      </div>
      {fin?.by_tender && (
        <div className="bs-admin-fin-tender" style={s(cardPanel + 'padding:18px;margin-bottom:24px')}>
          <div style={s('font-size:11px;font-weight:600;letter-spacing:0.1em;color:var(--bs-text-label);margin-bottom:12px')}>SALES BY TENDER</div>
          {fin.by_tender.map((t: any) => (
            <div key={t.tender} className="bs-admin-fin-row" style={s('display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--bs-line);font-size:13px;color:var(--bs-text-body)')}>
              <span>{t.tender.replace('_', ' ')}</span>
              <span>{t.orders} order(s) · {Number(t.points) > 0 ? fmtPoints(Number(t.points)) : fmtCents(Number(t.cents))}</span>
            </div>
          ))}
        </div>
      )}
      <div className="bs-admin-fin-recon" style={s(cardPanel + 'padding:18px')}>
        <div style={s('display:flex;align-items:center;justify-content:space-between;margin-bottom:12px')}>
          <div style={s('font-size:11px;font-weight:600;letter-spacing:0.1em;color:var(--bs-text-label)')}>RECONCILIATION</div>
          {recon && (
            <span style={{ ...s('font-size:12px;font-weight:700'), color: recon.clean ? 'var(--bs-green)' : 'var(--bs-red)' }}>
              {recon.clean ? 'CLEAN ✓' : 'MISMATCH — INVESTIGATE'}
            </span>
          )}
        </div>
        {(recon?.checks || []).map((c: any) => (
          <div key={c.name} className="bs-admin-fin-row" style={s('display:flex;justify-content:space-between;padding:7px 0;border-bottom:1px solid var(--bs-line);font-size:12.5px;color:var(--bs-text-body)')}>
            <span>{c.name.replace(/_/g, ' ')}</span>
            <span style={{ color: c.mismatches === 0 ? 'var(--bs-green)' : 'var(--bs-red)' }}>{c.mismatches} mismatch(es)</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ConfigTab: React.FC = () => {
  const [cfg, setCfg] = useState<any | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api.adminGetConfig().then(setCfg).catch(() => {}); }, []);
  if (!cfg) return <div style={s('color:var(--bs-text-muted)')}>Loading…</div>;

  const save = async () => {
    try {
      const next = await api.adminPutConfig({
        commission_rate: Number(cfg.commission_rate),
        price_points_min: Number(cfg.price_points_min),
        price_points_max: Number(cfg.price_points_max),
        price_cents_min: Number(cfg.price_cents_min),
        price_cents_max: Number(cfg.price_cents_max),
        reservation_ttl_seconds: Number(cfg.reservation_ttl_seconds),
      });
      setCfg(next); setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e: any) { alert(e.message); }
  };

  const field = (key: string, label: string, help: string, step?: string) => (
    <div key={key}>
      <label style={s(labelStyle)}>{label}<InfoTip text={help} /></label>
      <input type="number" step={step} value={cfg[key]} onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })} style={s(inputStyle)} />
    </div>
  );

  return (
    <div style={s(cardPanel + 'padding:26px;max-width:640px')}>
      <div style={s('display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px')}>
        {field('commission_rate', 'COMMISSION RATE (0–0.9)',
          "Nova's cut of every sale, as a fraction: 0.15 means the platform keeps 15% and the seller receives 85% of the sale price, settled in the buyer's currency (points or cash). Applies to future sales only — past splits are never recalculated.", '0.01')}
        {field('reservation_ttl_seconds', 'RESERVATION TTL (SECONDS)',
          'How long a beat is held exclusively for a buyer while their card / Apple Pay / Google Pay payment completes. If payment does not finish in this window, the hold expires and the beat returns to the Library automatically. Points purchases are instant and never use a reservation.')}
        {field('price_points_min', 'MIN PRICE — POINTS',
          'The lowest Nova Points price a seller may set when listing a beat. 1,000 ✦ ≈ $10 by the 1-point-per-cent convention.')}
        {field('price_points_max', 'MAX PRICE — POINTS',
          'The highest Nova Points price a seller may set when listing a beat. 1,000,000 ✦ ≈ $10,000.')}
        {field('price_cents_min', 'MIN PRICE — CENTS',
          'The lowest cash price a seller may set, in US cents: 1000 = $10.00. Every beat carries both a points and a cash price; the buyer picks the currency at checkout.')}
        {field('price_cents_max', 'MAX PRICE — CENTS',
          'The highest cash price a seller may set, in US cents: 500000 = $5,000.00.')}
      </div>
      <PrimaryBtn onClick={save}>{saved ? 'Saved ✓' : 'Save configuration'}</PrimaryBtn>
      <div style={s('margin-top:12px;font-size:11.5px;color:var(--bs-text-faint)')}>
        Changes take effect immediately for new listings and checkouts. Price bounds do not affect already-listed beats.
      </div>
    </div>
  );
};

const AuditTab: React.FC = () => {
  const [audit, setAudit] = useState<any[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.adminGetAudit({ limit: PAGE_SIZE, offset: page * PAGE_SIZE })
      .then((r) => {
        if (!cancelled) {
          setAudit(r.audit);
          setTotal(r.total || 0);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAudit([]);
          setTotal(0);
        }
      });
    return () => { cancelled = true; };
  }, [page]);

  const cols = '170px 160px 1fr 200px';
  return (
    <div>
      <div className="bs-admin-audit-head" style={headGrid(cols)}>
        <span style={th}>WHEN</span>
        <span className="bs-admin-audit-desktop" style={th}>ACTOR</span>
        <span style={th}>ACTION</span>
        <span className="bs-admin-audit-desktop" style={th}>SUBJECT</span>
      </div>
      {audit === null && <div style={s('color:var(--bs-text-muted);padding:24px 10px')}>Loading…</div>}
      {audit !== null && audit.length === 0 && (
        <div style={s('color:var(--bs-text-muted);padding:24px 10px')}>No audit entries.</div>
      )}
      {(audit || []).map((a) => (
        <div key={a.id} className="bs-admin-audit-row" style={rowGrid(cols)}>
          <span style={s('font-size:12px;color:var(--bs-text-muted)')}>{new Date(a.created_at).toLocaleString()}</span>
          <span className="bs-admin-audit-desktop" style={s('font-size:12.5px;color:var(--bs-text-body)')}>{a.actor_username || `user #${a.actor_user_id}`}</span>
          <span style={s('font-size:12.5px;color:var(--bs-text);font-weight:600')}>{a.action}</span>
          <span className="bs-admin-audit-desktop" style={s('font-size:11.5px;color:var(--bs-text-label);overflow:hidden;text-overflow:ellipsis;white-space:nowrap')}>{a.subject_type} {a.subject_id}</span>
          <span className="bs-admin-audit-mobile-meta" style={s('font-size:11px;color:var(--bs-text-label);margin-top:2px')}>
            {a.actor_username || `user #${a.actor_user_id}`} · {a.subject_type} {a.subject_id}
          </span>
        </div>
      ))}
      {audit !== null && (
        <ListPagination page={page} total={total} onPage={setPage} />
      )}
    </div>
  );
};

export default AdminPage;
